import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search,
  MapPin,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader,
  RefreshCw,
  Filter
} from 'lucide-react';

interface ScrapedSpot {
  name: string;
  description: string;
  category: string;
  mentioned_by: string;
  source_url: string;
  confidence_score: number;
  raw_text: string;
}

const RedditScraperDashboard: React.FC = () => {
  const [subreddit, setSubreddit] = useState('AskNYC');
  const [spots, setSpots] = useState<ScrapedSpot[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const API_BASE = 'https://biig-api.b-9f2.workers.dev';

  const scrapeSubreddit = async () => {
    setLoading(true);
    setSpots([]);
    setSummary('');
    
    try {
      const response = await fetch(`${API_BASE}/api/scraper/reddit/${subreddit}`);
      const data = await response.json();
      
      if (data.success) {
        setSpots(data.spots || []);
        setSummary(data.summary || '');
      } else {
        console.error('Scraping failed:', data.error);
      }
    } catch (error) {
      console.error('Error scraping:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickTest = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/scraper/test`);
      const data = await response.json();
      
      if (data.success) {
        setSpots(data.sampleExtraction || []);
        setSummary(data.formatted || '');
      }
    } catch (error) {
      console.error('Test failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(spots.map(s => s.category))];
  const filteredSpots = selectedCategory === 'all' 
    ? spots 
    : spots.filter(s => s.category === selectedCategory);

  const getCategoryEmoji = (category: string) => {
    const emojis: { [key: string]: string } = {
      food: '🍔',
      bar: '🍺',
      nature: '🌳',
      culture: '🎨',
      shopping: '🛍️',
      views: '📸',
      other: '📍'
    };
    return emojis[category] || '📍';
  };

  const getConfidenceColor = (score: number) => {
    if (score > 0.7) return 'text-green-400 bg-green-900/20';
    if (score > 0.5) return 'text-yellow-400 bg-yellow-900/20';
    return 'text-gray-400 bg-gray-900/20';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Search className="w-10 h-10 text-blue-400" />
            Reddit Hidden Gems Scraper
          </h1>
          <p className="text-gray-300">
            Automatically discover hidden spots from city subreddits
          </p>
        </motion.div>

        {/* Controls */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-2">Subreddit</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">r/</span>
                <input
                  type="text"
                  value={subreddit}
                  onChange={(e) => setSubreddit(e.target.value)}
                  className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg"
                  placeholder="AskNYC"
                />
              </div>
            </div>
            <div className="flex gap-2 items-end">
              <button
                onClick={scrapeSubreddit}
                disabled={loading || !subreddit}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Scrape
              </button>
              <button
                onClick={quickTest}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Test
              </button>
            </div>
          </div>

          {/* Quick suggestions */}
          <div className="flex gap-2">
            <span className="text-sm text-gray-400">Try:</span>
            {['AskNYC', 'AskSF', 'AskAustin', 'AskChicago', 'london'].map(sub => (
              <button
                key={sub}
                onClick={() => setSubreddit(sub)}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                r/{sub}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader className="w-12 h-12 text-blue-400 animate-spin mb-4" />
            <p className="text-gray-300">Scraping Reddit for hidden gems...</p>
            <p className="text-sm text-gray-500 mt-2">This may take 30-60 seconds</p>
          </div>
        )}

        {/* Category Filter */}
        {spots.length > 0 && (
          <div className="mb-6 flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <div className="flex gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {cat === 'all' ? 'All' : `${getCategoryEmoji(cat)} ${cat}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Grid */}
        {filteredSpots.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {filteredSpots.map((spot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-800 rounded-xl p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <span>{getCategoryEmoji(spot.category)}</span>
                    {spot.name}
                  </h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(spot.confidence_score)}`}>
                    {Math.round(spot.confidence_score * 100)}%
                  </span>
                </div>
                
                <p className="text-sm text-gray-300 mb-3">
                  {spot.description}
                </p>
                
                <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-400 italic">
                    "{spot.raw_text}"
                  </p>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>by u/{spot.mentioned_by}</span>
                  <a 
                    href={spot.source_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    View on Reddit →
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Summary */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-xl p-6"
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Scraping Summary
            </h3>
            <pre className="text-sm text-gray-300 whitespace-pre-wrap">
              {summary}
            </pre>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && spots.length === 0 && (
          <div className="text-center py-20">
            <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              Enter a subreddit and click "Scrape" to discover hidden gems
            </p>
            <p className="text-gray-500 text-sm mt-2">
              The scraper will search for mentions of hidden spots, secret places, and local favorites
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default RedditScraperDashboard;